const rawApiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000';

const TENANT_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/**
 * Lowercase and drop a trailing root dot. `acme.app.runcofactor.com.` is a
 * legal absolute FQDN a browser will keep verbatim in `location.hostname`;
 * without normalizing, it misses the tenant match and the SPA silently talks to
 * the build-time API host instead of the org's own — i.e. the user looks signed
 * out on their own subdomain.
 */
function normalizeHostname(hostname: string): string {
  const h = hostname.toLowerCase();
  return h.endsWith('.') ? h.slice(0, -1) : h;
}

/**
 * Parses a per-org tenant *app* host of shape `<label>.app.<rest>`, where
 * `<rest>` is itself a cofactor app suffix (`runcofactor.com` or
 * `<stage>.runcofactor.com`) — e.g. `acme.app.dev.runcofactor.com`. `label`
 * is validated with the same shape the backend uses for `app_subdomain`.
 * Returns null for every non-tenant host (shared cofactor app host, sandbox
 * `.env.` hosts, localhost). Used by `resolveApiUrl` to build the tenant's
 * same-site api origin, and by `AppShell` to name the generic app host on the
 * "Workspace Not Found" screen.
 */
export function parseTenantAppHost(hostname: string): { label: string; rest: string } | null {
  const h = normalizeHostname(hostname);
  const dotIndex = h.indexOf('.');
  if (dotIndex <= 0) return null;
  const label = h.slice(0, dotIndex);
  const rest = h.slice(dotIndex + 1);
  if (!TENANT_LABEL_PATTERN.test(label)) return null;
  if (!rest.startsWith('app.') || !rest.endsWith('.runcofactor.com')) return null;
  return { label, rest };
}

/**
 * The cofactor *app* suffix a tenant label would hang off, for the host the
 * page is served on — `app.runcofactor.com` on both the shared cofactor app
 * host and any tenant host under it.
 *
 * Returns null for every host where per-org subdomains do not exist: canary
 * hosts, sandbox `.env.` hosts, and localhost. Callers must treat null as "this
 * host cannot address a tenant org" rather than substituting the current
 * hostname — `<label>.app.trycanary.ai` and `<label>.localhost` resolve to
 * nothing, and the latter also silently drops the dev port.
 */
export function cofactorAppHostSuffix(hostname: string): string | null {
  const h = normalizeHostname(hostname);
  const tenant = parseTenantAppHost(h);
  if (tenant) return tenant.rest;
  if (h.startsWith('app.') && h.endsWith('.runcofactor.com')) return h;
  return null;
}

/**
 * Resolve the API base URL for the host the page is served on.
 *
 * The same web build is served on both the canary app host (app.trycanary.ai)
 * and the cofactor app host (app.runcofactor.com / app.dev.runcofactor.com) via
 * a CloudFront alias, so the build-time VITE_API_URL is fixed to the canary API.
 * When loaded on the cofactor *app* host, route to the same-site cofactor API
 * instead (app.* -> api.*). This keeps API calls same-site so the SameSite=Lax,
 * __Host- session cookies set by the OAuth callback are sent on later requests.
 *
 * The cofactor app host gets the app.->api. rewrite. Sandbox env hosts
 * (`<id>.env.<stage>.runcofactor.com` and the dev-only
 * `<id>.env.dev.trycanary.ai` alias) instead resolve /api at RUNTIME against
 * their own origin: web (/) and api (/api) are the same origin behind the
 * sandbox nginx proxy, so the build-time VITE_API_URL (`${SANDBOX_URL}/api`,
 * baked per-sandbox) is irrelevant. Resolving from window.location makes the
 * built `apps/web/dist` instance-independent, so one prebuilt bundle is reusable
 * across every sandbox (the cache prerequisite — Workstream B). Canary hosts and
 * everything else keep the build-time value.
 *
 * Per-org tenant app hosts (`<label>.app.<rest>`) take the same app.->api.
 * rewrite with the label carried through — see {@link parseTenantAppHost}.
 */
export function resolveApiUrl(
  host: string | undefined,
  protocol: string,
  buildTimeApiUrl: string
): string {
  const fallback = buildTimeApiUrl.replace(/\/+$/, '');
  if (!host) return fallback;
  // Normalized for EVERY arm, not just the tenant one below. A trailing-dot
  // shared app host or sandbox host would otherwise miss its own `endsWith`
  // check and fall through to the build-time API — the same "looks signed out"
  // failure the tenant arm normalizes to avoid. Safe for existing traffic
  // because this is a no-op on any host without a trailing dot.
  const h = normalizeHostname(host);
  const isCofactorAppHost =
    h === 'runcofactor.com' || (h.startsWith('app.') && h.endsWith('.runcofactor.com'));
  if (isCofactorAppHost) {
    return `${protocol}//${h.replace(/^app\./, 'api.')}`;
  }
  // Sandbox env hosts: same-origin /api at runtime, independent of the baked value.
  const isSandboxHost =
    (h.endsWith('.runcofactor.com') && h.includes('.env.')) || h.endsWith('.env.dev.trycanary.ai');
  if (isSandboxHost) {
    return `${protocol}//${h}/api`;
  }
  // Per-org tenant app hosts (<label>.app.<rest>) route same-site to the
  // matching tenant api host (<label>.api.<rest>) — same app.->api. swap as
  // the shared cofactor app host above, just with the label carried through.
  const tenant = parseTenantAppHost(h);
  if (tenant) {
    return `${protocol}//${tenant.label}.api.${tenant.rest.slice('app.'.length)}`;
  }
  return fallback;
}

export const API_URL =
  typeof window === 'undefined'
    ? rawApiUrl.replace(/\/+$/, '')
    : resolveApiUrl(window.location.hostname, window.location.protocol, rawApiUrl);

const rawDocsUrl = (import.meta.env.VITE_DOCS_URL as string | undefined) ?? 'http://localhost:5178';
export const DOCS_URL = rawDocsUrl.replace(/\/+$/, '');

export const IS_LOCAL = /localhost|127\.0\.0\.1/.test(API_URL);

export const TEST_LOGIN_DOMAIN =
  (import.meta.env.VITE_TEST_LOGIN_DOMAIN as string | undefined) ?? '';

const rawMcpUrl = (import.meta.env.VITE_MCP_URL as string | undefined) ?? 'http://localhost:4100';
export const MCP_URL = rawMcpUrl.replace(/\/+$/, '');
